import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { registrationSchema } from '@/lib/schemas/registrationSchema'
import { useSubmitRegistration } from '@/lib/api/queries/useRegistration'
import { useImageUpload } from '@/hooks/useImageUpload'
import type { RegistrationFormData } from '@/lib/types/registration'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EventInfoSection } from '@/components/registration/EventInfoSection'
import { PersonalDetailsSection } from '@/components/registration/PersonalDetailsSection'
import { TShirtDetailsSection } from '@/components/registration/TShirtDetailsSection'
import { SportsSelectionSection } from '@/components/registration/SportsSelectionSection'
import { SportsExpertiseSection } from '@/components/registration/SportsExpertiseSection'
import { FeeDisplayWidget } from '@/components/registration/FeeDisplayWidget'
import { PaymentInstructionsSection } from '@/components/registration/PaymentInstructionsSection'
import { ImageUploadWidget } from '@/components/registration/ImageUploadWidget'
import { DuplicateRegistrationDialog } from '@/components/registration/DuplicateRegistrationDialog'
import { APP_CONSTANTS } from '@/lib/constants/app'
import { Loader2, Trophy, ChevronRight } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useRegistrationStore } from '@/lib/store/registrationStore'

export function RegistrationPage() {
  const navigate = useNavigate()
  const {
    uploadProfilePhoto,
    uploadPaymentScreenshot,
    isUploadingProfilePhoto,
    isUploadingPaymentScreenshot,
    profilePhotoPreview,
    paymentScreenshotPreview,
  } = useImageUpload()

  // Use refs to avoid closure issues with form submission
  const uploadedPhotoUrlRef = useRef<string | null>(null)
  const uploadedPaymentUrlRef = useRef<string | null>(null)
  // Local state for UI updates
  const [uploadedPhotoUrl, setUploadedPhotoUrl] = useState<string | null>(null)
  const [uploadedPaymentUrl, setUploadedPaymentUrl] = useState<string | null>(null)

  // Sync with store reset (when user clicks "Register Another Player")
  const storePhotoUrl = useRegistrationStore((state) => state.profilePhotoUrl)
  const storePaymentUrl = useRegistrationStore((state) => state.paymentScreenshotUrl)

  useEffect(() => {
    if (!storePhotoUrl) {
      uploadedPhotoUrlRef.current = null
      setUploadedPhotoUrl(null)
    }
    if (!storePaymentUrl) {
      uploadedPaymentUrlRef.current = null
      setUploadedPaymentUrl(null)
    }
  }, [storePhotoUrl, storePaymentUrl])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      selectedSportIds: [],
    },
  })

  const { mutate: submitRegistration, isPending } = useSubmitRegistration()
  const selectedSportIds = watch('selectedSportIds') || []
  const fullName = watch('fullName')

  const handleProfilePhotoSelect = async (file: File) => {
    try {
      const url = await uploadProfilePhoto(file, fullName)
      uploadedPhotoUrlRef.current = url
      setUploadedPhotoUrl(url)
    } catch (error) {
      console.error('Upload failed:', error)
    }
  }

  const handlePaymentScreenshotSelect = async (file: File) => {
    try {
      const url = await uploadPaymentScreenshot(file, fullName)
      uploadedPaymentUrlRef.current = url
      setUploadedPaymentUrl(url)
    } catch (error) {
      console.error('Upload failed:', error)
    }
  }

  const onSubmit = async (data: RegistrationFormData) => {
    if (isUploadingProfilePhoto || isUploadingPaymentScreenshot) {
      alert('Please wait for images to finish uploading')
      return
    }

    // Use refs to get latest values (avoid closure issues)
    const photoUrl = uploadedPhotoUrlRef.current
    const paymentUrl = uploadedPaymentUrlRef.current

    if (!photoUrl || !paymentUrl) {
      alert('Please upload both your photo and payment screenshot')
      return
    }

    const sportsList = selectedSportIds.length > 0 ? `${selectedSportIds.length} sport(s)` : 'no sports'
    const confirmed = window.confirm(
      `You're registering for ${sportsList}. Do you wish to proceed?`
    )

    if (!confirmed) return

    submitRegistration(
      {
        formData: data,
        profilePhotoUrl: photoUrl,
        paymentScreenshotUrl: paymentUrl,
      },
      {
        onSuccess: (result) => {
          navigate('/success', {
            state: { isPickleballEnrolled: result.isPickleballEnrolled },
          })
        },
        onError: (error: any) => {
          if (error.message === 'DUPLICATE_REGISTRATION') {
            return
          }
          alert('Registration failed. Please try again.')
        },
      }
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Banner */}
      <div className="relative gradient-hero">
        {/* Banner Image Container */}
        <div className="w-full bg-primary/20">
          <img
            src="/ypl_banner.jpg"
            alt="YPL Season 11"
            className="w-full h-auto max-h-48 sm:max-h-56 md:max-h-64 lg:max-h-72 object-contain mx-auto"
          />
        </div>

        {/* Hero Content */}
        <div className="relative px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm font-medium mb-4">
              <Trophy className="w-4 h-4 text-amber-400" />
              <span>Season 11 is here!</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 tracking-tight">
              YPL Registration
            </h1>
            <p className="text-lg text-white/70 max-w-xl mx-auto">
              Join the most exciting sports tournament of the year. Register now and showcase your talent!
            </p>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Event Info */}
        <EventInfoSection />

        {/* Registration Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
          {/* Personal Details */}
          <PersonalDetailsSection register={register} watch={watch} errors={errors} />

          {/* T-Shirt Details */}
          <TShirtDetailsSection register={register} errors={errors} />

          {/* Sports Selection */}
          <SportsSelectionSection setValue={setValue} watch={watch} errors={errors} />

          {/* Sports Expertise */}
          <SportsExpertiseSection register={register} />

          {/* Fee Display */}
          {selectedSportIds.length > 0 && (
            <FeeDisplayWidget numberOfSports={selectedSportIds.length} />
          )}

          {/* Payment Instructions */}
          <PaymentInstructionsSection />

          {/* Document Upload */}
          <Card variant="premium" className="overflow-hidden">
            <div className="bg-gradient-to-r from-slate-50 to-slate-100/50 px-6 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-sm">📎</span>
                Upload Documents
              </h3>
            </div>
            <div className="p-6 space-y-6">
              <ImageUploadWidget
                label="Your Photo"
                imagePreview={profilePhotoPreview}
                isUploading={isUploadingProfilePhoto}
                isUploaded={!!uploadedPhotoUrl}
                onFileSelect={handleProfilePhotoSelect}
                maxSizeKB={APP_CONSTANTS.PROFILE_PHOTO_MAX_SIZE_KB}
              />

              <ImageUploadWidget
                label="Payment Screenshot"
                imagePreview={paymentScreenshotPreview}
                isUploading={isUploadingPaymentScreenshot}
                isUploaded={!!uploadedPaymentUrl}
                onFileSelect={handlePaymentScreenshotSelect}
                maxSizeKB={APP_CONSTANTS.PAYMENT_SCREENSHOT_MAX_SIZE_KB}
              />
            </div>
          </Card>

          {/* Submit Section */}
          <div className="pt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-amber-800 flex items-start gap-2">
                <span className="font-semibold">ℹ️</span>
                <span>
                  By submitting, you confirm that all the information provided is accurate and you agree to the tournament terms and conditions.
                </span>
              </p>
            </div>

            <Button
              type="submit"
              disabled={isPending || isUploadingProfilePhoto || isUploadingPaymentScreenshot}
              size="xl"
              className="w-full group"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting Registration...
                </>
              ) : (
                <>
                  Complete Registration
                  <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">
            © 2026 Yuva Premier League. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Duplicate Dialog */}
      <DuplicateRegistrationDialog />
    </div>
  )
}
